const DEFAULT_VARIANT_NAME = 'Default';

const uniqueCleanStrings = (values = []) => {
  const seen = new Set();

  return (Array.isArray(values) ? values : []).reduce((normalized, value) => {
    const cleaned = typeof value === 'string' ? value.trim() : String(value || '').trim();
    const key = cleaned.toLowerCase();

    if (cleaned && !seen.has(key)) {
      seen.add(key);
      normalized.push(cleaned);
    }

    return normalized;
  }, []);
};

export const normalizeProductSizes = (sizes = []) => uniqueCleanStrings(sizes);

export const normalizeProductVariants = (variants = []) => {
  const seen = new Set();

  return (Array.isArray(variants) ? variants : []).reduce((normalized, variant) => {
    if (!variant || typeof variant !== 'object') return normalized;

    const name = String(variant.name || '').trim() || DEFAULT_VARIANT_NAME;
    const key = name.toLowerCase();
    if (seen.has(key)) return normalized;

    seen.add(key);
    normalized.push({ ...variant, name });
    return normalized;
  }, []);
};

export const normalizeInventoryQuantity = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return 0;
  return Math.max(0, Math.floor(numericValue));
};

export const getVariantInventoryKey = (variantOrName = '') => {
  const rawName =
    typeof variantOrName === 'object'
      ? variantOrName?.name
      : variantOrName;
  const normalizedName = String(rawName || DEFAULT_VARIANT_NAME).trim() || DEFAULT_VARIANT_NAME;

  return encodeURIComponent(normalizedName).replace(/\./g, '%2E');
};

const getVariantInventoryRow = (variantInventory = {}, variantOrName = '') => {
  if (!variantInventory || typeof variantInventory !== 'object') return {};

  const rawName =
    typeof variantOrName === 'object'
      ? variantOrName?.name
      : variantOrName;
  const legacyKey = String(rawName || DEFAULT_VARIANT_NAME).trim() || DEFAULT_VARIANT_NAME;
  const normalizedKey = getVariantInventoryKey(variantOrName);

  return variantInventory[normalizedKey] || variantInventory[legacyKey] || {};
};

export const cleanVariantInventory = (variantInventory = {}, variants = [], sizes = []) => {
  const allowedSizes = new Set((Array.isArray(sizes) ? sizes : []).map((size) => String(size)));
  const options = Array.isArray(variants) && variants.length ? variants : [{ name: DEFAULT_VARIANT_NAME }];

  return options.reduce((inventory, option) => {
    const row = getVariantInventoryRow(variantInventory, option);
    const cleanedRow = {};

    allowedSizes.forEach((size) => {
      const quantity = normalizeInventoryQuantity(row[size]);
      if (quantity > 0) {
        cleanedRow[size] = quantity;
      }
    });

    if (Object.keys(cleanedRow).length > 0) {
      inventory[getVariantInventoryKey(option)] = cleanedRow;
    }

    return inventory;
  }, {});
};

export const getVariantInventoryTotal = (product = {}) => {
  const variantInventory = product?.variantInventory;
  if (!variantInventory || typeof variantInventory !== 'object') return 0;

  return Object.values(variantInventory).reduce((total, row) => {
    if (!row || typeof row !== 'object') return total;

    return total + Object.values(row).reduce(
      (rowTotal, quantity) => rowTotal + normalizeInventoryQuantity(quantity),
      0
    );
  }, 0);
};

export const hasVariantInventory = (product = {}) => getVariantInventoryTotal(product) > 0;

export const getVariantSizeStock = (product = {}, variantOrName = '', size = '') => {
  if (!hasVariantInventory(product)) return null;

  const row = getVariantInventoryRow(product?.variantInventory, variantOrName);
  return normalizeInventoryQuantity(row[String(size)]);
};

export const getVariantTotalStock = (product = {}, variantOrName = '') => {
  if (!hasVariantInventory(product)) return null;

  const row = getVariantInventoryRow(product?.variantInventory, variantOrName);
  return Object.values(row || {}).reduce(
    (total, quantity) => total + normalizeInventoryQuantity(quantity),
    0
  );
};
